import React from "react";
import moment from "moment";
import SurveyLocationMap from "./Assessments/SurveyLocationMap";
import { getInitials } from "../utils/strings";
import memoize from "lodash/memoize";

export const GETPATIENTS_FILTERFIELDS = "GETPATIENTS_FILTERFIELDS";
export const GETPATIENTS_SORTFIELDS = "GETPATIENTS_SORTFIELDS";
export const GETPATIENTS_DISPLAYFIELDS = "GETPATIENTS_DISPLAYFIELDS";
export const GETPATIENTS_DISPLAYFIELDS_BASIC =
  "GETPATIENTS_DISPLAYFIELDS_BASIC";
export const GETPATIENTS_DISPLAYFIELDS_CLINICALDATA =
  "GETPATIENTS_DISPLAYFIELDS_CLINICALDATA";
export const GETPATIENTS_DISPLAYFIELDS_REPORT =
  "GETPATIENTS_DISPLAYFIELDS_REPORT";
export const GETTASKS_FILTERFIELDS = "GETTASKS_FILTERFIELDS";
export const GETTASKS_SORTFIELDS = "GETTASKS_SORTFIELDS";
export const GETTASKS_DISPLAYFIELDS = "GETTASKS_DISPLAYFIELDS";

// export const PATIENTS_FILTER = "PATIENTS_FILTER";
// export const PATIENTS_WATCHLIST_FILTER = "PATIENTS_WATCHLIST_FILTER";

export function getCurrentMilestone(carePlan) {
  if (!carePlan) {
    return null;
  }
  return carePlan.carePlanMileStones.filter((m) => {
    return m.current === true;
  })[0];
}

export function getCarePlanReadyForEdit(carePlan) {
  let mileStones = carePlan.carePlanMileStones || carePlan.mileStones || [];
  let mileStonesMap = {},
    milestoneIds = [];
  mileStones.forEach((milestone) => {
    milestone.id = milestone.id || milestone._id;
    mileStonesMap[milestone.id] = milestone;
    milestoneIds.push(milestone.id);
    let actions = milestone.actions || [];
    actions.forEach((action) => {
      action.id = action.id || action._id;
      prepareActivity(action.activity);
      parseCondition(action.condition);
    });
  });

  carePlan.mileStones = mileStonesMap;
  carePlan.keys = milestoneIds;
  return carePlan;
}

export function getStatusCount(statusStats, status) {
  let s = statusStats.filter((stat) => stat.stat === status)[0];
  return s ? s.count : 0;
}

// Helper function for getting number range
function _getNumberRange(number) {
  // Handle special cases
  if (number < 0) {
    return "< 0";
  }
  if (number > 100) {
    return "101+";
  }

  // For ranges from 0-100, calculate more efficiently
  const rangeStart = Math.floor(number / 10) * 10;
  const rangeEnd = rangeStart + 10;

  return `${rangeStart} - ${rangeEnd}`;
}

// Memoize for better performance
export const getNumberRange = memoize(_getNumberRange);

export function createAgeRangeMap(data = {}) {
  let keys = Object.keys(data);
  keys.sort((a, b) => {
    return parseInt(a) - parseInt(b);
  });

  let newMap = {};
  keys.forEach((key) => {
    let newKey = getNumberRange(parseInt(key));
    if (newMap[newKey]) {
      newMap[newKey] = newMap[newKey] + data[key];
    } else {
      newMap[newKey] = data[key];
    }
  });

  return newMap;
}
export function augmentActivityIds(activity) {
  if (!activity) {
    return;
  }

  if (activity.template) {
    activity.artifactId = activity.template.value;
    delete activity.template;
  }

  if (activity.surveyId) {
    activity.artifactId = activity.surveyId;
    delete activity.surveyId;
    delete activity.surveyTitle;
  }

  if (activity.libraryId) {
    activity.artifactId = activity.libraryId;
    delete activity.libraryId;
    delete activity.libraryName;
  }

  if (activity.carePlanId) {
    activity.artifactId = activity.carePlanId;
    delete activity.carePlanId;
    delete activity.carePlanTitle;
  }

  if (activity.consentDocumentId) {
    activity.artifactId = activity.consentDocumentId;
    delete activity.consentDocumentId;
    delete activity.consentDocumentTitle;
  }

  if (activity.taskTemplateId) {
    activity.artifactId = activity.taskTemplateId;
    delete activity.taskTemplateId;
    delete activity.taskTemplateTitle;
  }
}

export function formatCondition(condition) {
  if (!condition) {
    return condition;
  }
  let { parameters = [] } = condition;
  condition.parameters = parameters.map((p) => {
    if (p && typeof p === "object") {
      return p.value;
    }
    return p;
  });
  return condition;
}

export function prepareActivity(activity) {
  if (!activity) {
    return;
  }
  if (activity.type === "MESSAGE_TEMPLATE") {
    activity.template = { value: activity.artifactId };
  }
  if (activity.type === "SURVEY") {
    activity.surveyId = activity.artifactId;
  }
  if (activity.type === "LIBRARY") {
    activity.libraryId = activity.artifactId;
  }
  if (activity.type === "ASSIGN_CAREPLAN") {
    activity.carePlanId = activity.artifactId;
  }
  if (activity.type === "CONSENT_DOCUMENT") {
    activity.consentDocumentId = activity.artifactId;
  }

  if (activity.type === "TASK_TEMPLATE") {
    activity.taskTemplateId = activity.artifactId;
  }
}

export function parseCondition(condition) {
  if (!condition) {
    return condition;
  }
  let { parameters = [] } = condition;
  condition.parameters = parameters.map((p) => {
    return { value: p };
  });
  return condition;
}

export function ordinal_suffix_of(i) {
  var j = i % 10,
    k = i % 100;
  if (j === 1 && k !== 11) {
    return i + "st";
  }
  if (j === 2 && k !== 12) {
    return i + "nd";
  }
  if (j === 3 && k !== 13) {
    return i + "rd";
  }
  return i + "th";
}

export function getParticipantsInfo(participants = []) {
  return participants.map((participant) => {
    if (participant.referenceType === "MASTERDATA_KEY") {
      return participant.masterDataValue.display;
    }
    return participant.displayHumanName
      ? participant.displayHumanName.text
      : "";
  });
}

export function getParticipantsInfoInitials(participants = [], shortName) {
  return participants.map((participant) => {
    if (participant.referenceType === "MASTERDATA_KEY") {
      if(participant.masterDataKey === "IVChairs"){
        return participant.masterDataValue.display;
      }
      return shortName
        ? participant.masterDataValue.display?.slice(0, 6)
        : getInitials(participant.masterDataValue.display);
    }
    return participant.displayHumanName
      ? participant.displayHumanName.text
        ? shortName
          ? participant.displayHumanName?.text.slice(0, 6)
          : getInitials(participant.displayHumanName.text)
        : ""
      : "";
  });
}

// Helper function to create appointment time options
function _getAppointmentTimeOptions() {
  // Preallocate array for better performance
  const options = [];
  for (let hour = 6; hour <= 23; hour++) {
    for (let min = 0; min <= 45; min = min + 15) {
      const hourStr = hour >= 10 ? `${hour}` : `0${hour}`;
      const minStr = min < 10 ? `0${min}` : `${min}`;
      const timeStr = `${hourStr}:${minStr}`;
      options.push({ value: timeStr, label: timeStr });
    }
  }
  return options;
}

// Helper function to create hour options
function _getHourOptions() {
  // Preallocate array with exact size
  const options = new Array(24);
  for (let hour = 0; hour <= 23; hour++) {
    const hourStr = hour >= 10 ? `${hour}` : `0${hour}`;
    options[hour] = { value: hourStr, label: hourStr };
  }
  return options;
}

// Helper function to create minute options
function _getMinOptions() {
  // Preallocate array with exact size
  const options = new Array(60);
  for (let min = 0; min <= 59; min++) {
    const minStr = min >= 10 ? `${min}` : `0${min}`;
    options[min] = { value: minStr, label: minStr };
  }
  return options;
}

// Memoized versions - these only need to be computed once since they never change
export const getAppointmentTimeOptions = memoize(_getAppointmentTimeOptions);
export const getHourOptions = memoize(_getHourOptions);
export const getMinOptions = memoize(_getMinOptions);

// Original function as a non-exported helper
function _getSurveyAnswerValue(itemType, ans) {
  //(typeof itemType, itemType);
  if (itemType === "INTEGER") {
    return ans.valueInteger || ans.valueAsString;
  }
  if (itemType === "DECIMAL") {
    return ans.valueDouble || ans.valueAsString;
  }
  if (itemType === "CHOICE") {
    return ans.valueString;
  }
  if (itemType === "CHOICE_WITH_SCALE") {
    return ans.valueInteger || ans.valueAsString;
  }
  if (itemType === "BOOLEAN") {
    return ans.valueBoolean ? "True" : "False";
  }
  if (itemType === "DATE" && ans.valueDate) {
    return new Date(ans.valueDate).toLocaleDateString();
  }
  if (itemType === "DATE_TIME" && ans.valueDateTime) {
    return new Date(ans.valueDateTime).toLocaleString();
  }
  if (itemType === "LOCATION") {
    return <SurveyLocationMap ans={ans} />;
  }
  if (itemType === "TIME_SERIES_DOUBLE") {
    return ans.valueDouble || ans.valueAsString;
  }
  if (itemType === "TIME_SERIES_INTEGER") {
    return ans.valueInteger || ans.valueAsString;
  }
  if (itemType === "TIME_SERIES_STRING") {
    return ans.valueSring || ans.valueAsString;
  }
  return ans.valueString;
}

// Create a memoized resolver key function
const getSurveyAnswerValueKey = (itemType, ans) => {
  // Special case for Location to ensure React components don't break memoization
  if (itemType === "LOCATION") {
    return `${itemType}-${JSON.stringify(ans)}`;
  }

  // Generate a key based on type and main value fields
  return `${itemType}-${ans.valueInteger || ""}-${ans.valueDouble || ""}-${
    ans.valueString || ""
  }-${ans.valueBoolean || ""}-${ans.valueDate || ""}-${
    ans.valueDateTime || ""
  }`;
};

// Export a memoized version with the same signature
export const getSurveyAnswerValue = memoize(
  _getSurveyAnswerValue,
  getSurveyAnswerValueKey
);

export function getSystemField(dataField) {
  if (dataField === "PatientContactPhone") {
    return "PHONE";
  } else if (dataField === "PatientContactTel") {
    return "TEL";
  } else if (dataField === "PatientContactEmail") {
    return "EMAIL";
  } else if (dataField === "GP Email") {
    return "EMAIL";
  } else if (dataField === "GP Phone") {
    return "PHONE";
  }
}

export function getSlotFilter(
  filter = {},
  scheduleId,
  start,
  end,
  countOnly,
  allStatus
) {
  // Use URLSearchParams for better performance with parameter building
  const params = new URLSearchParams();
  const { location, staffId } = filter;

  // Use final scheduleId (from parameter or filter)
  const finalScheduleId = scheduleId || filter.scheduleId;

  // Only add parameters that have values
  if (location) params.append("location", location);
  if (staffId && staffId !== "ALL") params.append("staffId", staffId);
  if (finalScheduleId) params.append("scheduleId", finalScheduleId);
  if (start) params.append("start", start);
  if (end) params.append("end", end);
  if (countOnly) params.append("countOnly", "true");
  if (allStatus) params.append("allStatus", allStatus);

  // Convert to string and add leading & if needed
  const paramString = params.toString();
  return paramString ? `&${paramString}` : "";
}

export function getAppointmentsFilter(
  filter = {},
  scheduleId,
  patientId,
  allStatus,
  start,
  end,
  seriesId
) {
  let { location, staffId, careTeamId } = filter;
  // Use URLSearchParams for better performance with parameter building
  const params = new URLSearchParams();

  // Use final scheduleId (from parameter or filter)
  const finalScheduleId = scheduleId || filter.scheduleId;

  // Only add parameters that have values using proper URLSearchParams methods
  if (location) params.append("location", location);
  if (staffId && staffId !== "ALL") params.append("staffId", staffId);
  if (careTeamId && careTeamId !== "ALL")
    params.append("careTeamId", careTeamId);
  if (finalScheduleId) params.append("scheduleId", finalScheduleId);
  if (patientId) params.append("patientId", patientId);
  if (allStatus)
    params.append(
      "allStatus",
      allStatus === "CANCELLED" ? "CANCELLED" : "true"
    );
  if (start) params.append("start", start);
  if (end) params.append("end", end);
  if (seriesId) params.append("seriesId", seriesId);

  // Convert to string and add leading & if needed
  const paramString = params.toString();
  return paramString ? `&${paramString}` : "";
}

export function isUrlValid(url) {
  var res = url.match(
    /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g
  );
  return res != null;
}

export function getAcceptableMediaTypes(type) {
  let acceptableMediaTypes = null;
  if (type === "IMAGE") {
    acceptableMediaTypes = ".png,.jpg,.gif";
  } else if (type === "AUDIO") {
    acceptableMediaTypes = ".mp3,.aac";
  } else if (type === "VIDEO") {
    acceptableMediaTypes = ".mp4,.avi";
  } else if (type === "PDF") {
    acceptableMediaTypes = ".pdf";
  }

  return acceptableMediaTypes;
}

// MYP-9472: media type(s) each known extension may be sent as (mp4/webm can be audio or video)
const AUDIO_OR_VIDEO = ["AUDIO", "VIDEO"];
const MEDIA_TYPES_BY_EXTENSION = {
  pdf: ["PDF"],
  jpg: ["IMAGE"], jpeg: ["IMAGE"], png: ["IMAGE"], gif: ["IMAGE"], webp: ["IMAGE"], heic: ["IMAGE"], bmp: ["IMAGE"],
  mp3: ["AUDIO"], aac: ["AUDIO"], m4a: ["AUDIO"], wav: ["AUDIO"],
  mov: ["VIDEO"], avi: ["VIDEO"], wmv: ["VIDEO"],
  mp4: AUDIO_OR_VIDEO, webm: AUDIO_OR_VIDEO,
};

// The picker's "All files" bypasses accept - so reject any file whose extension cannot be
// the chosen media type. Unknown extensions are left to the backend's file validation.
export function hasMismatchedMediaFile(messageType, files) {
  return Array.from(files || []).some((file) => {
    const allowed = MEDIA_TYPES_BY_EXTENSION[file.name.toLowerCase().split(".").pop()];
    return allowed && !allowed.includes(messageType);
  });
}

export function getNoOfFreeSlots(appointment) {
  let { participants = [], maxPatientsAllowed = 1 } = appointment;
  let patients = participants.filter((p) => p.referenceType === "PATIENT");
  return maxPatientsAllowed - patients.length;
}

export function exportArtifact(artifact) {
  let dataStr =
    "data:text/json;charset=utf-8," +
    encodeURIComponent(JSON.stringify(artifact));
  const link = window.document.createElement("a");
  link.href = dataStr;
  link.setAttribute("download", `${artifact.name}.json`);
  window.document.body.appendChild(link);
  link.click();
}

export const TIME_HOUR_OPTIONS = [
  { value: 0, label: "12 AM" },
  { value: 1, label: "1 AM" },
  { value: 2, label: "2 AM" },
  { value: 3, label: "3 AM" },
  { value: 4, label: "4 AM" },
  { value: 5, label: "5 AM" },
  { value: 6, label: "6 AM" },
  { value: 7, label: "7 AM" },
  { value: 8, label: "8 AM" },
  { value: 9, label: "9 AM" },
  { value: 10, label: "10 AM" },
  { value: 11, label: "11 AM" },
  { value: 12, label: "12 PM" },
  { value: 13, label: "1 PM" },
  { value: 14, label: "2 PM" },
  { value: 15, label: "3 PM" },
  { value: 16, label: "4 PM" },
  { value: 17, label: "5 PM" },
  { value: 18, label: "6 PM" },
  { value: 19, label: "7 PM" },
  { value: 20, label: "8 PM" },
  { value: 21, label: "9 PM" },
  { value: 22, label: "10 PM" },
  { value: 23, label: "11 PM" },
];

export function isItSlot(event) {
  return event.hasOwnProperty("slotStatus");
}

export function isItCountSlot(event) {
  return event.hasOwnProperty("count") && event.hasOwnProperty("statDate");
}

export function isMedicationDocument(document) {
  let title = document.title;
  if (title) {
    title = title.toLowerCase();
    if (title.indexOf("medication") > -1) {
      let contents = document.contents || [];
      if (contents[0] && document.contents[0].format === "IMAGE") {
        return true;
      }
    }
  }
  return false;
}

export const severityColorLevel = (value) => {
  const newValue = value?.trim("").toUpperCase();

  const levels = {
    NONE: "green",
    MILD: "#FFD63F",
    MODERATE: "orange",
    SEVERE: "red",
    "VERY SEVERE": "red",
    0: "green",
    1: "#FFD63F",
    2: "orange",
    3: "red",
  };

  return levels[newValue];
};

export const severityTextLevel = (value) => {
  const levels = {
    0: "None",
    1: "Mild",
    2: "Moderate",
    3: "Severe",
    4: "Very severe",
  };

  return levels[value];
};

export function hexToRGBA(hex, alpha = 1) {
  let r = 0,
    g = 0,
    b = 0;

  // Check if hex is null or not a valid hex string
  if (
    !hex ||
    !(hex.length === 4 || hex.length === 7) ||
    !/^#([0-9A-F]{3}){1,2}$/i.test(hex)
  ) {
    return "rgba(0, 0, 255, 0.4)"; // Default value
  }

  // 3 digits
  if (hex.length === 4) {
    r = "0x" + hex[1] + hex[1];
    g = "0x" + hex[2] + hex[2];
    b = "0x" + hex[3] + hex[3];

    // 6 digits
  } else if (hex.length === 7) {
    r = "0x" + hex[1] + hex[2];
    g = "0x" + hex[3] + hex[4];
    b = "0x" + hex[5] + hex[6];
  }

  return `rgba(${+r}, ${+g}, ${+b}, ${alpha})`;
}

export const JOIN_CALL_LEAD_MINUTES = 5;

export function canJoinScheduledCall(start, end) {
  if (!start) return true;
  const now = moment();
  const opens = moment(start).subtract(JOIN_CALL_LEAD_MINUTES, "minutes");
  const closes = end
    ? moment(end)
    : moment(start).add(60, "minutes");
  return now.isSameOrAfter(opens) && now.isBefore(closes);
}
